import React, { useState, useRef } from 'react';
import {
  CREATE_LAND_TITLE,
  DELETE_TITLE_DOCUMENT,
  EDIT_MINERAL_OWNERSHIP,
  EDIT_SURFACE_OWNERSHIP,
  EDIT_TITLE_DOCUMENT,
  GET_TRACT,
  TRACTS_QUERY,
  MAP_QUERY,
} from '../../components/tracts/TractQueries';
import TractMapCard from '../../components/tracts/TractMapCard';
import EditTractForm from './editTractForm/EditTractForm';
import AddIcon from '@material-ui/icons/Add';
import LoadingView from '../../components/LoadingView';
import {
  Typography,
  Tab,
  Tabs,
  AppBar,
  Toolbar,
  Button,
} from '@material-ui/core';
import Auth from '../../util/Auth';
import { makeStyles } from '@material-ui/styles';
import { useSnackbar } from 'notistack';
import { DELETE_TRACT, EDIT_TRACT } from '../../components/tracts/TractQueries';
import { EDIT_WELL } from '../../components/wells/WellQueries';
import { useQuery, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import LandWellsTable from './LandWellsTable';
import LandROWTable from './LandROWTable';
import LandBooks from './LandBooks';
import ChainOfTitleTable from './ChainOfTitleTable';
import math from 'mathjs';
import OGLTable from './OGLTable';
import PERMISSIONS_LIST from '../../util/RolesEnum';
import OtherLeasesTable from './editTractForm/OtherLeasesTable';
import DeleteTractModal from './DeleteTractModal';
import Uploader from '../../util/Uploader';
import { STATE_LIST } from '../../components/admin/adminQueries';

const hasPermissions = Auth.hasRole(PERMISSIONS_LIST.WRITE.WRITE_LANDS);

const useStyles = makeStyles((theme) => ({
  isAuthorized: {
    display: hasPermissions ? 'block' : 'none !important',
  },
  unauthorized: {
    display: hasPermissions ? 'none' : 'block',
  },
  title: {
    color: '#1976d2',
    fontSize: '1.75rem',
    textAlign: 'center',
    textDecoration: 'underline',
    align: 'center',
  },
}));

const TitleFragment = gql`
  fragment TitleFragment on Tract {
    id
    titleRun {
      id
      volPage
      book
      dateFiled
      effectiveDate
      docType
      comments
      grantor
      grantee
      nsaTotal
      nmaTotal
      fileName
    }
  }
`;

const propertyValues = (fraction) => (gross) => {
  const decimal = math.number(math.fraction(fraction));
  return {
    net: math.multiply(decimal, gross),
    decimal: math.multiply(decimal, 100),
  };
};

const submitValues = (values) => ({
  ...values,
  grossMineralAcres: parseFloat(values.grossMineralAcres),
  grossSurfaceAcres: parseFloat(values.grossSurfaceAcres),
  mineralOwnershipPercentage: propertyValues(values.mineralFraction)(
      values.grossMineralAcres
  ).decimal.toFixed(8),
  surfaceOwnershipPercentage: propertyValues(values.surfaceFraction)(
      values.grossSurfaceAcres
  ).decimal.toFixed(8),
  familyNMA: propertyValues(values.mineralFraction)(
      values.grossMineralAcres
  ).net.toFixed(8),
  familyNSA: propertyValues(values.surfaceFraction)(
      values.grossSurfaceAcres
  ).net.toFixed(8),
  mineralOwnership: values.grossMineralAcres !== 0,
  surfaceOwnership: values.grossSurfaceAcres !== 0,
});

function TabContainer(props) {
  return (
      <Typography component='div'>
        {props.children}
      </Typography>
  );
}

const GetTractFromQuery = (queryResult) => {
  if (!Array.isArray(queryResult) || queryResult.length === 0) {
    return false;
  }
  return queryResult[0];
};

function EditTract({ match, history }) {
  const [, setLoading] = React.useState(false);

  const classes = useStyles(undefined);
  const { data, error, loading } = useQuery(GET_TRACT, {
    variables: {
      id: match.params.tractID,
    },
  });
  const StateListQuery = useQuery(STATE_LIST);
  const { data: mapData } = useQuery(MAP_QUERY);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [UpdateTract] = useMutation(EDIT_TRACT);
  const [editWellCoords] = useMutation(EDIT_WELL);

  const handleWellPositionChange = (well, { lat, lng }) => {
    editWellCoords({
      variables: {
        id: well.id,
        apiNumber: well.apiNumber,
        latitude: String(lat),
        longitude: String(lng),
        latitudeBottom: String(well.latitudeBottom ?? lat),
        longitudeBottom: String(well.longitudeBottom ?? lng),
      },
    })
      .then(() => enqueueSnackbar(`Well "${well.name}" repositioned`, { variant: 'success' }))
      .catch((err) => enqueueSnackbar(err.toString(), { variant: 'error' }));
  };

  const [DeleteTract] = useMutation(DELETE_TRACT, {
    update: (store, { data: { DeleteTract } }) => {
      try {
        const LandQuery = store.readQuery({ query: TRACTS_QUERY });

        const newData = LandQuery.Tract.filter(
            (tract) => tract.id !== DeleteTract.id
        );

        store.writeQuery({
          query: TRACTS_QUERY,
          data: { Tract: newData },
        });
      } catch (e) {
        console.log('TRACTS_QUERY has not been run yet!');
      }
    },
  });

  const [addTitle] = useMutation(CREATE_LAND_TITLE);
  const [removeTitle] = useMutation(DELETE_TITLE_DOCUMENT);
  const [updateTitle] = useMutation(EDIT_TITLE_DOCUMENT);
  const [editMinerals] = useMutation(EDIT_MINERAL_OWNERSHIP);
  const [editSurface] = useMutation(EDIT_SURFACE_OWNERSHIP);

  // For the DeleteTractModal
  const [modalOpen, setModalOpen] = useState(false);

  const handleDelete = () => {
    DeleteTract({
      variables: { id: editableTract.id },
    })
        .then(() => {
          enqueueSnackbar('Successfully deleted Tract', { variant: 'success' });
          history.push('/Lands');
        })
        .catch((err) => {
          console.error(err);
          enqueueSnackbar(err.toString(), { variant: 'error' });
        });
  };

  const updateSurfaceOwnership = (values) =>
      editSurface({ variables: values });

  const updateMineralOwnership = (values) =>
      editMinerals({ variables: values });

  const editTitleDoc = (values) => updateTitle({ variables: values });

  const createTitleDoc = (values) => addTitle({ variables: values });

  const deleteTitleDoc = (values) =>
      removeTitle({
        variables: values,
        update: (store, { data: { DeleteClause } }) => {
          try {
            const oldTitle = store.readFragment({
              id: match.params.tractID,
              fragment: TitleFragment,
            });

            const updatedTitle = oldTitle.titleRun.filter(
                (title) => values.id !== title.id
            );

            store.writeFragment({
              id: match.params.tractID,
              fragment: TitleFragment,
              data: {
                ...oldTitle,
                titleRun: updatedTitle,
              },
            });
          } catch (e) {
            console.error(e);
          }
        },
      });

  const savedTab = parseInt(localStorage.getItem('activeTab') || '0', 10);
  const [value, setValue] = useState(savedTab);
  const [mapEditMode, setMapEditMode] = useState(false);
  const kmzUploadRef = useRef(null);

  if (error) {
    console.error(error);
    return <h1>{error.toString()}</h1>;
  }
  if (loading) {
    return <LoadingView />;
  }

  const Tract = GetTractFromQuery(data.Tract) || [];
  const editableTract = { ...Tract }; // or Object.assign({}, Tract)


  return (
      <div style={{
        display: 'fixed',
        flexDirection: 'column',
        height: '100vh',
        overflowY: 'auto'
      }}>
        <AppBar position='fixed'
                style={{
                  display: "flex",
                  marginBottom: 0,
                  paddingTop: value < 2 ? 68 : 56,
                  height: value < 2 ? 150 : 120
                }}
        >
          <Toolbar
              style={{
                marginTop: 0,
                paddingTop: 0,
                position: "static",
                border: 'black',
                width: '100%',
                justifyContent: 'space-between',
                alignSelf: 'flex-start',
                paddingLeft: 215
              }}
          >
            <Tabs
                value={value}
                variant="scrollable"
                scrollButtons="auto"
                onChange={(e, newVal) => {
                  setValue(newVal);
                  localStorage.setItem('activeTab', newVal);
                }}
            >
              <Tab label='Main' style={{ minWidth: 1, padding: '0 0px' }} />
              <Tab label='Map' style={{ minWidth: 1, padding: '0 0px' }} />
              <Tab label='Land Books' style={{ minWidth: 1, padding: '0 0px' }} />
              <Tab label='Chain of Title' style={{ minWidth: 1, padding: '0 0px' }}/>
              <Tab label='Wells' style={{ minWidth: 1, padding: '0 0px' }}/>
              <Tab label='ROW' style={{ minWidth: 1, padding: '0 0px' }}/>
              <Tab label='OGL(S)' style={{ minWidth: 1, padding: '0 0px' }}/>
              <Tab label='Other Leases' style={{ minWidth: 1, padding: '0 0px' }}/>
            </Tabs>
            {/* BUTTONS on the right */}
            {value === 0 && hasPermissions && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', alignSelf: 'center' }}>
                  <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      type="submit"
                      form="editTractForm"
                      className={classes.isAuthorized}
                      style={{ fontSize: '0.75rem', minWidth: 'auto', padding: '4px 10px' }}
                  >
                    Update
                  </Button>
                  <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      className={classes.isAuthorized}
                      onClick={() => setModalOpen(true)}
                      style={{ fontSize: '0.75rem', minWidth: 'auto', padding: '4px 10px' }}
                  >
                    Delete
                  </Button>
                </div>
            )}
            {value === 1 && hasPermissions && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', alignSelf: 'center' }}>
                  <Button
                      variant="contained"
                      size="small"
                      className={classes.isAuthorized}
                      onClick={() => setMapEditMode((prev) => !prev)}
                      style={{
                        fontSize: '0.75rem',
                        minWidth: 'auto',
                        padding: '4px 10px',
                        backgroundColor: mapEditMode ? '#2e7d32' : '#555',
                        color: '#fff',
                      }}
                  >
                    {mapEditMode ? 'Done Editing' : 'Edit Shape'}
                  </Button>
                  <Button
                      variant="contained"
                      size="small"
                      className={classes.isAuthorized}
                      onClick={() => kmzUploadRef.current?.click()}
                      style={{ fontSize: '0.75rem', minWidth: 'auto', padding: '4px 10px', backgroundColor: '#555', color: '#fff' }}
                  >
                    Upload KMZ/KML
                  </Button>
                </div>
            )}
            {value === 3 && hasPermissions && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', alignSelf: 'center' }}>
                  <Uploader
                      className={classes.isAuthorized}
                      itemID={editableTract.id}
                      updateFunction={(newData) => createTitleDoc({
                        ...newData,
                        tractID: editableTract.id
                      })}
                      typeOfUploader={"TRACT"}
                      history={history}
                      setValue={setValue}
                      setLoading={setLoading}
                  />
                  <AddIcon
                      className={classes.isAuthorized}
                      style={{ fontSize: '30px', color: '#fff', float: 'right', cursor: 'pointer' }}
                      onClick={() => {
                        const plusDiv = document.getElementById('myChain');
                        if (plusDiv) plusDiv.click();
                      }}
                      fontSize="small"
                  />
                </div>
            )}
          </Toolbar>
        </AppBar>
        <div style={{ paddingTop: '80px', position: "static", width: "100%", }} >
          <DeleteTractModal
              open={modalOpen}
              handleClose={() => setModalOpen(false)}
              handleAgree={handleDelete}
          />
          {value === 0 && (
              <TabContainer>
                <EditTractForm
                    Tract={editableTract}
                    history={history}
                    EditTract={UpdateTract}
                    DeleteTract={DeleteTract}
                    StateCounties={{states: StateListQuery.data}}
                    updateMineralOwnership={updateMineralOwnership}
                    updateSurfaceOwnership={updateSurfaceOwnership}
                    snackbarControls={{ open: enqueueSnackbar, close: closeSnackbar }}
                    submitValues={submitValues}
                />
              </TabContainer>
          )}
          {value === 1 && (
              <TabContainer>
                <TractMapCard
                    tractID={editableTract.id}
                    initialCoords={(() => {
                      try {
                        const geo = JSON.parse(editableTract.geometry);
                        if (Array.isArray(geo) && geo.length > 0) {
                          return geo.map(([lng, lat]) => [lat, lng]);
                        }
                      } catch (e) {}
                      return [];
                    })()}
                    onCoordsChange={(newCoords) => {
                      const newGeometry = JSON.stringify(newCoords.map(([lat, lng]) => [lng, lat]));
                      UpdateTract({ variables: submitValues({ ...editableTract, geometry: newGeometry }) })
                          .then(() => enqueueSnackbar('Tract map saved!', { variant: 'success' }))
                          .catch((err) => enqueueSnackbar(err.toString(), { variant: 'error' }));
                    }}
                    tractInfo={{
                      name: editableTract.name,
                      state: editableTract.state,
                      county: editableTract.county,
                      acreage: editableTract.acreage,
                      grossMineralAcres: editableTract.grossMineralAcres,
                      grossSurfaceAcres: editableTract.grossSurfaceAcres,
                      mineralFraction: editableTract.mineralFraction,
                      familyNMA: editableTract.familyNMA,
                      survey: editableTract.survey,
                      abstract: editableTract.abstract,
                      legalDescription: editableTract.legalDescription,
                      color: editableTract.color,
                    }}
                    wells={editableTract.drilledOn || []}
                    allTracts={mapData?.Tract || []}
                    uploadRef={kmzUploadRef}
                    editMode={mapEditMode}
                    onWellPositionChange={handleWellPositionChange}
                />
              </TabContainer>
          )}
          {value === 2 && (
              <TabContainer>
                <LandBooks
                    Tract={editableTract}
                    match={match}
                />
              </TabContainer>
          )}
          {value === 3 && (
              <TabContainer>
                <ChainOfTitleTable
                    Tract={editableTract}
                    queries={{
                      createTitle: createTitleDoc,
                      deleteTitle: deleteTitleDoc,
                      editTitle: editTitleDoc,
                    }}
                    match={match}
                />
              </TabContainer>
          )}
          {value === 4 && (
              <TabContainer>
                <LandWellsTable Land={editableTract} />
              </TabContainer>
          )}
          {value === 5 && (
              <TabContainer>
                <LandROWTable Land={editableTract} />
              </TabContainer>
          )}
          {value === 6 && (
              <TabContainer>
                <OGLTable Tract={editableTract} />
              </TabContainer>
          )}
          {value === 7 && (
              <TabContainer>
                <OtherLeasesTable Tract={editableTract} />
              </TabContainer>
          )}

          <Typography variant='h5' className={classes.unauthorized}>
            Not Authorized To Make Changes
          </Typography>
        </div>
      </div>
  );
}

export default EditTract;